"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { Button } from "@/app/components/ui/button";
import { Switch } from "@/app/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/app/components/ui/accordion";
import { X } from "lucide-react";
import { useConsentStore } from "@/app/stores/consent-store";
import {
  readConsentAction,
  saveCustomConsentAction,
  acceptAllConsentAction,
  rejectAllConsentAction,
} from "@/app/services/server/actions/consent";
import type { ConsentPreferencesT } from "@/app/types/consent";
import { runConsentRevocationCleanup } from "@/app/utils/helpers/consent/cleanup";
import * as Sentry from "@sentry/nextjs";
import { updateSentryBasedOnConsent } from "@/instrumentation-client";

function getStaticCookiesByCategory(category: string): string[] {
  const cookieMap: Record<string, string[]> = {
    necessary: ["cookie_consent"],
    functional: ["Sentry"],
    analytics: ["Google Analytics (_ga, _gid, _gat)", "Google Tag Manager"],
    marketing: ["Facebook Pixel", "YouTube Videos"],
  };

  return cookieMap[category] || [];
}

export default function CookiePreferencesDialog() {
  const {
    isCustomizeOpen,
    setCustomizeOpen,
    setConsent,
    consent,
    setBannerOpen,
  } = useConsentStore();
  const [localPrefs, setLocalPrefs] = useState<ConsentPreferencesT | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isCustomizeOpen) return;
    (async () => {
      try {
        const stored = await readConsentAction();
        setLocalPrefs(stored.preferences);
      } catch (error) {
        // Network aborts (e.g. Safari "Load failed") must not surface as unhandled rejections
        console.error("Failed to read consent state", error);
        Sentry.captureException(error, { tags: { flow: "consent" } });
      }
    })();
  }, [isCustomizeOpen]);

  const handleSave = async () => {
    if (!localPrefs) return;
    setIsSaving(true);
    try {
      // Cleanup revoked categories before persisting
      const stored = await readConsentAction();
      runConsentRevocationCleanup(stored.preferences, localPrefs);
      const next = await saveCustomConsentAction(localPrefs);
      setConsent(next);
      updateSentryBasedOnConsent(next);
      setCustomizeOpen(false);

      // Reload only if cookies were disabled (not when enabled)
      const categories: (keyof typeof localPrefs)[] = [
        "functional",
        "analytics",
        "marketing",
      ];
      const wasSomethingDisabled = categories.some(
        (cat) => stored.preferences[cat] && !localPrefs[cat]
      );

      if (wasSomethingDisabled) {
        window.location.reload();
      }
    } catch (error) {
      console.error("Failed to save consent", error);
      Sentry.captureException(error, { tags: { flow: "consent" } });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAcceptAll = async () => {
    setIsSaving(true);
    try {
      const next = await acceptAllConsentAction();
      setConsent(next);
      updateSentryBasedOnConsent(next);
      setCustomizeOpen(false);
    } catch (error) {
      console.error("Failed to save consent", error);
      Sentry.captureException(error, { tags: { flow: "consent" } });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRejectAll = async () => {
    setIsSaving(true);
    try {
      // Cleanup all optional categories
      const stored = await readConsentAction();
      runConsentRevocationCleanup(stored.preferences, {
        ...stored.preferences,
        functional: false,
        analytics: false,
        marketing: false,
        necessary: true,
      });
      const next = await rejectAllConsentAction();
      setConsent(next);
      updateSentryBasedOnConsent(next);
      setCustomizeOpen(false);

      // Reload page to ensure all cookie changes take effect immediately
      window.location.reload();
    } catch (error) {
      console.error("Failed to save consent", error);
      Sentry.captureException(error, { tags: { flow: "consent" } });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setCustomizeOpen(open);
    // Show banner again if closing dialog and no decision was made yet
    if (!open && !consent?.decided) {
      setBannerOpen(true);
    }
  };

  return (
    <Dialog open={isCustomizeOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[80vh] w-[94%] max-w-xl flex-col sm:w-full"
        showCloseButton={false}
      >
        <DialogHeader className="just flex w-full flex-row items-center justify-between gap-2">
          <DialogTitle>Cookie Präferenzen</DialogTitle>
          <Button
            variant="secondary"
            size="icon"
            className="bg-muted border-muted h-8 w-8 rounded-full"
            onClick={() => {
              setCustomizeOpen(false);
              // Show banner again if no decision was made yet
              if (!consent?.decided) {
                setBannerOpen(true);
              }
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Schließen</span>
          </Button>
        </DialogHeader>

        <div className="relative flex-1 overflow-y-auto pb-8">
          <Accordion type="multiple" className="w-full space-y-2 pr-2">
            <AccordionItem value="necessary" className="rounded-lg border px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="mr-4 flex w-full items-center justify-between">
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">Notwendig</p>
                      <span className="text-muted-foreground bg-muted rounded-md px-2 py-1 text-xs font-medium">
                        Immer aktiv
                      </span>
                    </div>
                    <p className="text-muted-foreground mt-0.5 pr-8 text-xs">
                      Diese Cookies sind erforderlich, um grundlegende
                      Funktionen der Website zu ermöglichen.
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  <div className="text-xs">
                    <p className="mb-1 font-medium">Verwendete Cookies:</p>
                    <ul className="text-muted-foreground space-y-1">
                      {getStaticCookiesByCategory("necessary").map(
                        (cookieName, index) => (
                          <li key={index}>
                            • <strong>{cookieName}</strong>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="functional"
              className="rounded-lg border px-4"
            >
              <div className="flex items-center justify-between">
                <AccordionTrigger className="mr-4 flex-1 hover:no-underline">
                  <div className="text-left">
                    <p className="text-sm font-medium">Personalisierung</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      Cookies, die es der Website ermöglichen, sich an Ihre
                      Entscheidungen zu erinnern.
                    </p>
                  </div>
                </AccordionTrigger>
                <Switch
                  checked={!!localPrefs?.functional}
                  onCheckedChange={(checked) =>
                    setLocalPrefs((p) =>
                      p ? { ...p, functional: checked, necessary: true } : p
                    )
                  }
                />
              </div>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  <div className="text-xs">
                    <p className="mb-1 font-medium">Verwendete Cookies:</p>
                    <ul className="text-muted-foreground space-y-1">
                      {getStaticCookiesByCategory("functional").map(
                        (cookieName, index) => (
                          <li key={index}>
                            • <strong>{cookieName}</strong>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="analytics" className="rounded-lg border px-4">
              <div className="flex items-center justify-between py-0">
                <AccordionTrigger className="mr-4 flex-1 hover:no-underline">
                  <div className="text-left">
                    <p className="text-sm font-medium">Analytics</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      Hilft uns zu verstehen, wie die Website genutzt wird und
                      ob es Probleme gibt.
                    </p>
                  </div>
                </AccordionTrigger>
                <Switch
                  checked={!!localPrefs?.analytics}
                  onCheckedChange={(checked) =>
                    setLocalPrefs((p) =>
                      p ? { ...p, analytics: checked, necessary: true } : p
                    )
                  }
                />
              </div>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  <div className="text-xs">
                    <p className="mb-1 font-medium">Verwendete Cookies:</p>
                    <ul className="text-muted-foreground space-y-1">
                      {getStaticCookiesByCategory("analytics").map(
                        (cookieName, index) => (
                          <li key={index}>
                            • <strong>{cookieName}</strong>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="marketing"
              className="rounded-lg border px-4 last:border-b"
            >
              <div className="flex items-center justify-between py-2">
                <AccordionTrigger className="mr-4 flex-1 hover:no-underline">
                  <div className="text-left">
                    <p className="text-sm font-medium">Marketing</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      Für Werbung und personalisierte Inhalte.
                    </p>
                  </div>
                </AccordionTrigger>
                <Switch
                  checked={!!localPrefs?.marketing}
                  onCheckedChange={(checked) =>
                    setLocalPrefs((p) =>
                      p ? { ...p, marketing: checked, necessary: true } : p
                    )
                  }
                />
              </div>
              <AccordionContent>
                <div className="space-y-2 pt-2">
                  <div className="text-xs">
                    <p className="mb-1 font-medium">Verwendete Cookies:</p>
                    <ul className="text-muted-foreground space-y-1">
                      {getStaticCookiesByCategory("marketing").map(
                        (cookieName, index) => (
                          <li key={index}>
                            • <strong>{cookieName}</strong>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="from-background pointer-events-none absolute right-0 bottom-49 left-0 h-8 bg-gradient-to-t to-transparent sm:bottom-21"></div>

        <div className="mt-0 flex flex-wrap-reverse justify-between gap-2 sm:flex-wrap">
          <Button
            className="w-full sm:w-auto"
            variant="secondary"
            onClick={handleRejectAll}
            disabled={isSaving}
          >
            Alle ablehnen
          </Button>
          <div className="flex w-full flex-wrap-reverse gap-2 sm:w-auto sm:flex-wrap">
            <Button
              className="w-full sm:w-auto"
              onClick={handleSave}
              disabled={isSaving}
            >
              Speichern
            </Button>
            <Button
              className="w-full sm:w-auto"
              onClick={handleAcceptAll}
              disabled={isSaving}
            >
              Alle aktivieren
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
